// src/Root.tsx
import { Suspense, useEffect, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import '@/App.scss';

import dayjs from 'dayjs';
import enUS from 'antd/locale/en_US';
import trTR from 'antd/locale/tr_TR';
import { useTranslation } from 'react-i18next';
import { ModalProvider } from './core/context/ModalContext';
import { useAuthInit } from './core/hooks/useAuthInit';

import 'dayjs/locale/en';
import 'dayjs/locale/tr';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CenteredSpinner } from '@/components/CenteredState';
import { AppToast } from '@/components/Toast';
import { darkTheme, lightTheme } from '@/config/themeConfig';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

function Root() {
  const { t, i18n } = useTranslation(['common']);
  const currentTheme = useSelector((state: any) => state.theme.themeMode);
  const themeConfig = currentTheme === 'light' ? lightTheme : darkTheme;
  const { initialized } = useAuthInit();

  useEffect(() => {
    document.documentElement.dataset.theme = currentTheme === 'dark' ? 'dark' : 'light';
  }, [currentTheme]);

  // Uppercase labels follow the active language (Turkish dotted İ).
  useEffect(() => {
    document.documentElement.lang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'tr';
  }, [i18n.language]);

  const locale = useMemo(() => {
    const lang = i18n.language;
    dayjs.locale(lang === 'tr' ? 'tr' : 'en');
    return lang === 'tr' ? trTR : enUS;
  }, [i18n.language]);

  const customizedLocale = useMemo(() => {
    return {
      ...locale,
      Table: {
        ...locale.Table,
        filterConfirm: t('common:common.table.filter'),
        filterReset: t('common:common.table.clear'),
      },
    };
  }, [locale, t]);

  // Static `Modal.confirm` calls render outside this tree; give them the same theme and locale.
  useEffect(() => {
    ConfigProvider.config({
      holderRender: (children) => (
        <ConfigProvider theme={themeConfig} locale={customizedLocale}>
          {children}
        </ConfigProvider>
      ),
    });
  }, [themeConfig, customizedLocale]);

  dayjs.locale(localStorage.getItem('i18nextLng') === 'tr' ? 'tr' : 'en');

  if (!initialized) {
    return <CenteredSpinner />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="css-var-r0">
        <ConfigProvider theme={themeConfig} locale={customizedLocale}>
          <ModalProvider>
            <Suspense fallback={<CenteredSpinner />}>
              <Outlet />
            </Suspense>
            <AppToast />
          </ModalProvider>
        </ConfigProvider>
      </div>
    </QueryClientProvider>
  );
}

export default Root;
